import User from '../../types/User';
import api from '../api/api';
import { PasswordRequest } from '../../types/PasswordRequest';
import LoginResponse from '../../types/LoginResponse';
import { UpdatePasswordRequest } from '../../types/UpdatePasswordRequest';
import { UpdateDetailsRequest } from '../../types/UpdateDetailsRequest';
import { UpdateDetailsResponse } from '../../types/UpdateDetailsResponse';
import GetUsersConfig from '../../types/GetUsersConfig';
import { UsersSearchResult } from '../../types/UsersSearchResult';
import EmailOnlyRequest from '../../types/EmailOnlyRequest';
import ContextUser from '../../types/ContextUser';
import UserRole from '../../types/UserRole';
import ResetTokenStatus from '../../types/ResetTokenStatus';

class UserService {
  static async loggedInUser(signal?: AbortSignal): Promise<ContextUser> {
    const apiResponse = await api.get('/users/me', { signal });
    const { user, userAuth } = apiResponse.data;
    const loggedInUser: ContextUser = {
      ...user,
      ...userAuth
    };
    return loggedInUser;
  }

  static async getUser(id: number, signal?: AbortSignal): Promise<User> {
    const apiResponse = await api.get(`/users/${id}`, { signal });
    return apiResponse.data;
  }

  static async setPassword(
    password: string,
    passwordResetToken: string | null
  ): Promise<LoginResponse> {
    const request: PasswordRequest = {
      password,
      passwordResetToken
    };

    const apiResponse = await api.post('/auth/set-password', request);
    const loginResponse: LoginResponse = apiResponse.data;
    localStorage.setItem('token', loginResponse.token);
    return loginResponse;
  }

  static async validateResetToken(
    passwordResetToken: string
  ): Promise<ResetTokenStatus> {
    const apiResponse = await api.get('/auth/validate-reset-token', {
      params: { token: passwordResetToken }
    });
    return apiResponse.data;
  }

  static async resetPassword(email: string): Promise<string> {
    const request: EmailOnlyRequest = {
      email
    };

    const apiResponse = await api.post('/auth/send-password-reset', request);
    const successMessage: string = apiResponse.data;
    return successMessage;
  }

  static async updatePassword(
    currentPassword: string,
    newPassword: string
  ): Promise<string> {
    const request: UpdatePasswordRequest = {
      currentPassword,
      newPassword
    };

    const apiResponse = await api.post('/auth/update-password', request);
    const updatePasswordResponse: string = apiResponse.data;
    return updatePasswordResponse;
  }

  static async updateUserDetails(
    email: string,
    firstName: string,
    lastName: string
  ): Promise<UpdateDetailsResponse> {
    const request: UpdateDetailsRequest = {
      email,
      firstName,
      lastName
    };

    const apiResponse = await api.put('/users/me', request);
    const updatePasswordResponse: UpdateDetailsResponse = apiResponse.data;
    localStorage.setItem('token', updatePasswordResponse.token);
    return updatePasswordResponse;
  }

  static async getUsers({
    searchQuery,
    sortDirection,
    sortColumn,
    page,
    resultsPerPage,
    organisationId,
    signal
  }: GetUsersConfig): Promise<UsersSearchResult> {
    const baseUrl = '/users';

    //  Append search params from arguments
    const searchParams = new URLSearchParams();
    searchParams.append(
      'sortDirection',
      sortDirection === null ? '' : sortDirection
    );
    searchParams.append('sortColumn', sortColumn);
    searchParams.append('page', page.toString());
    searchParams.append('resultsPerPage', resultsPerPage.toString());
    if (organisationId)
      searchParams.append('organisationId', organisationId.toString());
    if (searchQuery) searchParams.append('q', searchQuery);
    //  Retrieve data
    const apiResponse = await api.get(`${baseUrl}?${searchParams}`, { signal });
    const conversationSearchResult = apiResponse.data;
    return conversationSearchResult;
  }

  static async addUser(
    email: string,
    firstName: string,
    lastName: string,
    role: UserRole,
    organisationId?: number
  ): Promise<string> {
    const baseUrl = '/users';
    // TODO: Add role to request
    const request: {
      email: string;
      firstName: string;
      lastName: string;
      role: UserRole;
      organisationId?: number;
    } = {
      email,
      firstName,
      lastName,
      role,
      organisationId
    };
    const apiResponse = await api.post(baseUrl, request);
    return apiResponse.data;
  }

  static async updateAnyUserDetails(
    id: number,
    email: string,
    firstName: string,
    lastName: string,
    organisationId: number
  ): Promise<string> {
    const baseUrl = '/users';
    const request: {
      email: string;
      firstName: string;
      lastName: string;
      organisationId: number;
    } = {
      email,
      firstName,
      lastName,
      organisationId
    };
    const apiResponse = await api.put(`${baseUrl}/${id}`, request);
    return apiResponse.data;
  }

  static async resendInviteEmail(
    email: string,
    organisationId?: number
  ): Promise<string> {
    const baseUrl = '/users/resend-invite';
    const request: { email: string; organisationId?: number } = {
      email,
      organisationId
    };
    const apiResponse = await api.post(baseUrl, request);
    return apiResponse.data;
  }

  static async deleteUser(
    id: number,
    organisationId?: number
  ): Promise<string> {
    const baseUrl = 'users';
    const url = organisationId
      ? `${baseUrl}/${id}?organisationId=${organisationId}`
      : `${baseUrl}/${id}`;
    const apiResponse = await api.delete(url);
    return apiResponse.data;
  }

  static async updateUserRole(
    id: number,
    role: UserRole
  ): Promise<ContextUser> {
    const baseUrl = 'users';
    const request: { role: UserRole } = {
      role
    };
    const apiResponse = await api.patch(`${baseUrl}/${id}`, request);
    const { user, userAuthStatus } = apiResponse.data;
    const loggedInUser: ContextUser = {
      ...user,
      ...userAuthStatus
    };
    return loggedInUser;
  }

  static async updateUserPreferences(
    selectedChatbotId: number | null
  ): Promise<User> {
    const request = {
      selectedChatbotId
    };
    const apiResponse = await api.patch('/users/me/preferences', request);
    return apiResponse.data;
  }
}

export default UserService;
